import React, { useMemo } from 'react';
import { format, isSameMonth, parseISO } from 'date-fns';
import { 
  BookOpen, 
  Calendar as CalendarIcon, 
  Users, 
  CheckCircle, 
  AlertCircle,
  MapPin,
  Clock,
  Download
} from 'lucide-react';

const CalendarList = ({ events, onEventClick, currentDate }) => {
  // Group events by Month-Year
  const groupedEvents = useMemo(() => {
    // 1. Sort by date
    const sorted = [...events].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    
    // 2. Group
    const groups = {};
    sorted.forEach(event => {
      const date = new Date(event.startDate);
      const key = format(date, 'MMMM yyyy');
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    });
    
    return groups;
  }, [events]);

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'exam': return <BookOpen className="h-4 w-4" />;
      case 'holiday': return <CalendarIcon className="h-4 w-4" />;
      case 'event': return <Users className="h-4 w-4" />;
      case 'meeting': return <Users className="h-4 w-4" />;
      case 'workshop': return <BookOpen className="h-4 w-4" />;
      case 'orientation': return <Users className="h-4 w-4" />;
      case 'result_day': return <CheckCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'exam': return 'bg-red-100 text-red-700 border-red-200';
      case 'holiday': return 'bg-green-100 text-green-700 border-green-200';
      case 'event': return 'bg-[color-mix(in_srgb,var(--primary-color,#4f46e5)_10%,white)] text-[var(--primary-color,#4f46e5)] border-[color-mix(in_srgb,var(--primary-color,#4f46e5)_20%,white)]';
      case 'meeting': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'workshop': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'orientation': return 'bg-[color-mix(in_srgb,var(--secondary-color,#7c3aed)_10%,white)] text-[var(--secondary-color,#7c3aed)] border-[color-mix(in_srgb,var(--secondary-color,#7c3aed)_20%,white)]';
      case 'result_day': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDateRange = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    if (s.toDateString() === e.toDateString()) {
      return format(s, 'MMM d, yyyy');
    }
    return `${format(s, 'MMM d')} - ${format(e, 'MMM d, yyyy')}`;
  };

  if (events.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
        <div className="h-16 w-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <CalendarIcon className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900">No Events Found</h3>
        <p className="text-gray-500">Try adjusting your filters or add a new event.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(groupedEvents).map(([month, monthEvents]) => {
        // Highlight current month group
        const isCurrentMonth = month === format(currentDate, 'MMMM yyyy');

        return (
          <div key={month} className={`relative ${isCurrentMonth ? '' : 'opacity-80 hover:opacity-100 transition-opacity'}`}>
            <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm py-1.5 px-3 border-b border-gray-200 mb-3 flex items-center justify-between">
              <h3 className="text-base font-bold" style={isCurrentMonth ? { color: 'var(--primary-color, #4f46e5)' } : { color: '#374151' }}>
                {month}
              </h3>
              <span className="text-xs font-medium bg-white px-2 py-0.5 rounded border border-gray-200 text-gray-500">
                {monthEvents.length} events
              </span>
            </div>

            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {monthEvents.map(event => (
                <div 
                  key={event._id}
                  onClick={() => onEventClick(event)}
                  className="bg-white rounded p-3 border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className={`px-2 py-0.5 rounded-full text-[10px] font-medium border flex items-center space-x-1 ${getEventTypeColor(event.type)}`}>
                      {getEventTypeIcon(event.type)}
                      <span className="capitalize">{event.type.replace('_', ' ')}</span>
                    </div>
                    {event.isRecurring && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full border" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}>
                        Recurring
                      </span>
                    )}
                  </div>

                  <h4 className="text-sm font-bold text-gray-900 mb-0.5 transition-colors line-clamp-1" onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color, #4f46e5)'} onMouseOut={(e) => e.currentTarget.style.color = '#111827'}>
                    {event.title}
                  </h4>
                  
                  <div className="flex items-center text-xs text-gray-500 mb-2 space-x-1.5">
                    <Clock className="h-3 w-3" />
                    <span>{formatDateRange(event.startDate, event.endDate)}</span>
                  </div>

                  {/* Highlights (Exam subjects or attachments) */}
                  <div className="space-y-1">
                    {event.examSchedule?.length > 0 && (
                      <div className="flex items-center text-[10px] text-gray-600 bg-gray-50 p-1 rounded">
                        <BookOpen className="h-3 w-3 mr-1.5" />
                        <span>{event.examSchedule.length} Exams scheduled</span>
                      </div>
                    )}
                    {event.attachments?.length > 0 && (
                      <div className="flex items-center text-[10px] text-gray-600 bg-gray-50 p-1 rounded">
                        <Download className="h-3 w-3 mr-1.5" />
                        <span>{event.attachments.length} Files</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarList;
